
import { useGammaWebServices } from "@/services";
import { computed, defineComponent, onMounted, ref, toRaw, watch } from "vue";
import Spinner from "@/components/shared/Spinner.vue";
import {
  Chart,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  LineController,
  ChartDataset,
} from "chart.js";
Chart.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  LineController
);
export default defineComponent({
  components: { Spinner },
  setup() {
    const gammaWebStore = useGammaWebServices();
    const isLoading = computed(() => gammaWebStore.isRiskChartDataLoading);

    const scenarioMap = computed(
      () =>
        gammaWebStore.riskChartData?.JBAScenarios.reduce(
          (x, y) => x.set(y.Id, y.EndYear),
          new Map<string, string>()
        ) || new Map<string, string>()
    );
    const chartCanvas = ref<HTMLCanvasElement | undefined>();
    let chart: Chart;

    onMounted(() => {
      watch(
        computed(() => gammaWebStore.riskChartData),
        () => loadChart(),
        { immediate: true }
      );
    });

    function loadChart() {
      if (!gammaWebStore.riskChartData) return;

      const labels = gammaWebStore.riskChartData.RiskSummaries.map((risk) =>
        scenarioMap.value.get(risk.Period)
      );
      const datasets: any[] = [
        generateCombineChartData(),
        ...generateRisksChartData(),
      ];

      if (chart) {
        chart.data.labels = labels;
        chart.data.datasets = datasets;
        chart.update();
      } else if (chartCanvas.value) {
        chart = new Chart(chartCanvas.value, {
          type: "line",
          data: {
            labels,
            datasets,
          },
          options: {
            responsive: true,
            plugins: {
              legend: {
                position: "bottom",
              },
            },
          },
        });
      }
    }

    function generateCombineChartData(): ChartDataset {
      return {
        label: "Combine",
        data:
          gammaWebStore.riskChartData?.RiskSummaries.map(
            (risk) =>
              ({
                x: scenarioMap.value.get(risk.Period),
                y: risk.JBACombinedRisk.CombinedScore,
                risk: toRaw(risk),
              } as any)
          ) || [],
        borderColor: "#17A2B8",
        backgroundColor: "#17A2B8",
        pointStyle: "circle",
        pointRadius: 10,
        pointHoverRadius: 10,
        pointBackgroundColor: (context) =>
          getColor(context.raw.risk.JBACombinedRisk.RAGScore.Color),
      };
    }

    function generateRisksChartData(): ChartDataset[] {
      const props = [
        ["Coastal", "CombinedScoreCoastal"],
        ["River", "CombinedScoreRiver"],
        ["Surface Water", "CombinedScoreSurfaceWater"],
      ];

      return props.map(([name, propName]) => {
        return {
          label: name,
          data:
            gammaWebStore.riskChartData?.RiskSummaries.map(
              (risk) => risk.JBACombinedRisk[propName]
            ) || [],
          borderColor: "#17b8ac",
          backgroundColor: "#17b8ac",
          pointStyle: "circle",
          borderDash: [5, 5],
        };
      });
    }

    function getColor(ragColor?: string) {
      switch (ragColor) {
        case "Red":
          return "#DC3545";

        case "Green":
          return "#20C997";

        case "Amber":
          return "#f80";
        default:
          return "#1d1d1d";
      }
    }

    return { chartCanvas, isLoading, loadChart };
  },
});
